/* eslint-disable react/jsx-no-bind */
// @flow
import React, { useState, type Node } from 'react';

import {
    InlineField,
    Flex,
    InlineFieldAction,
    InlineFieldActions,
    InlineFieldInfoAction,
    type InlineFieldProps
} from '@locus-taxy/neo-ui';
import DateTimePicker from 'common/components/core/form/DateTimePicker';
import theme from './styles.scss';
import DateTimeUtils, {
    CustomDateFormats,
    TimeStyle
} from 'common/utils/DateTimeUtils';
import TimeUtils from 'common/utils/TimeUtils';
import warning from 'common/utils/warning';

type TimeFieldTypes = {
    ...InlineFieldProps,
    onChange: (value: string) => void,
    info?: string,
    min?: number | string,
    max?: number | string,
    timeStep?: number
};

const DEFAULT_TIME_STEP = 15;

const TimeField = ({
    label,
    value,
    onChange,
    size = 'medium',
    fullWidth,
    info,
    helperText,
    error,
    disabled,
    min,
    max,
    validated,
    required = false,
    timeStep = DEFAULT_TIME_STEP,
    icon = 'stopwatch'
}: TimeFieldTypes): Node => {
    warning(
        !value || typeof value === 'string',
        'Only string input is supported in TimeField. Other types are deprecated'
    );
    const [isInputFocused, setIsFocused] = useState(false);
    const handleOnChange = (newValue: Moment) => {
        const formattedValue = DateTimeUtils.customFormat(
            newValue,
            CustomDateFormats.API_TIME
        );

        onChange(formattedValue);
    };

    const handleFocus = (event: SyntheticInputEvent<HTMLInputElement>) => {
        event.target.setAttribute('autocomplete', 'off');
        setIsFocused(true);
    };
    const handleBlur = () => setIsFocused(false);
    const handleValueReset = () => onChange('');

    const timeFormat = DateTimeUtils.getFormat({
        timeStyle: TimeStyle.SHORT
    });

    const momentValue = TimeUtils.getMoment(value, CustomDateFormats.API_TIME);

    return (
        <InlineField
            label={label}
            icon={icon}
            size={size}
            isInputFocused={isInputFocused}
            fullWidth={fullWidth}
            value={value}
            helperText={helperText}
            disabled={disabled}
            error={error}
            validated={validated}
            required={required}
        >
            <Flex alignItems="center" py={2} width="100%">
                <DateTimePicker
                    timeFormat={timeFormat}
                    value={momentValue || null}
                    onChange={handleOnChange}
                    timeStep={timeStep}
                    timePlaceholder={isInputFocused ? timeFormat : ''}
                    showDate={false}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    theme={theme}
                    disabled={disabled}
                    min={min}
                    max={max}
                />
                <InlineFieldActions>
                    {info ? <InlineFieldInfoAction info={info} /> : null}
                    {momentValue ? (
                        <InlineFieldAction
                            icon="cross_circle"
                            onClick={handleValueReset}
                        />
                    ) : null}
                </InlineFieldActions>
            </Flex>
        </InlineField>
    );
};
export { TimeField, TimeField as default };
